import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Calculator, Monitor } from "lucide-react";

export default function LEDEstimatorPage() {
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");

  const widthNum = parseFloat(width) || 0;
  const heightNum = parseFloat(height) || 0;

  // N = (Width - 4) / 12.59, rounded up
  const n = widthNum > 4 ? Math.ceil((widthNum - 4) / 12.59) : 0;
  // Y = (Height - 4) / 6.295, rounded up
  const y = heightNum > 4 ? Math.ceil((heightNum - 4) / 6.295) : 0;

  // Actual dimensions
  const actualWidth = n > 0 ? (4 + n * 12.59).toFixed(1) : "0";
  const actualHeight = y > 0 ? (4 + y * 6.295).toFixed(1) : "0";

  const price = n * y * 115;
  const hasResult = n > 0 && y > 0;

  return (
    <div data-testid="led-estimator-page">
      {/* Hero */}
      <section className="pt-28 pb-12 sm:pt-36 sm:pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">
            Pricing Tool
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6">
            LED Display <span className="gold-shimmer">Estimator</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-sm sm:text-base text-white/50 max-w-2xl leading-relaxed">
            Get an instant price estimate for your programmable LED display sign. Enter your desired dimensions and we'll calculate the cost.
          </motion.p>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Input */}
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-gold" />
                </div>
                <h2 className="font-heading text-xl font-bold text-white">Enter Dimensions</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-white/60 text-xs block mb-2">Desired Width (inches)</label>
                  <input
                    type="number"
                    data-testid="led-width-input"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-4 text-lg placeholder:text-white/20 focus:ring-1 focus:ring-gold/30 transition-all outline-none"
                    placeholder="e.g. 48"
                    min="5"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs block mb-2">Desired Height (inches)</label>
                  <input
                    type="number"
                    data-testid="led-height-input"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-4 text-lg placeholder:text-white/20 focus:ring-1 focus:ring-gold/30 transition-all outline-none"
                    placeholder="e.g. 24"
                    min="5"
                  />
                </div>
              </div>

              {hasResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 pt-8 border-t border-white/10"
                >
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/50">Modules (horizontal x vertical)</span>
                      <span className="text-white font-medium">{n} x {y}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">Actual Sign Width</span>
                      <span className="text-white font-medium">{actualWidth}"</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">Actual Sign Height</span>
                      <span className="text-white font-medium">{actualHeight}"</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Result */}
            <div className="space-y-6">
              <div className="glass-card rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-gold" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-white">Estimated Price</h2>
                </div>

                {hasResult ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <p className="font-heading text-5xl sm:text-6xl font-bold text-gold mb-2">
                      ${price.toLocaleString()}
                    </p>
                    <p className="text-white/40 text-sm mb-8">
                      Based on {n} x {y} LED modules ({actualWidth}" x {actualHeight}")
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link
                        to="/booking"
                        data-testid="led-book-btn"
                        className="flex-1 bg-gold text-black font-bold py-3 rounded-full text-sm text-center hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all"
                      >
                        Book Consultation
                      </Link>
                      <a
                        href="tel:+17783220801"
                        data-testid="led-call-btn"
                        className="flex-1 border border-white/20 text-white py-3 rounded-full text-sm text-center hover:bg-white/5 hover:border-gold/50 transition-all flex items-center justify-center gap-2"
                      >
                        <Phone className="w-3.5 h-3.5" /> Call Now
                      </a>
                    </div>
                  </motion.div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white/30 text-sm">Enter width and height to see your estimate</p>
                  </div>
                )}
              </div>

              <div className="glass-card rounded-2xl p-6">
                <h3 className="font-heading text-sm font-bold text-white mb-3">Important Notes</h3>
                <ul className="space-y-2 text-xs text-white/50">
                  <li>This is an estimate. Final pricing may vary based on installation requirements.</li>
                  <li>Minimum width: 5 inches. Minimum height: 5 inches.</li>
                  <li>Price includes the LED display unit. Installation quoted separately.</li>
                  <li>Contact us for custom configurations and bulk pricing.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gold/5"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,191,0,0.4)] to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
              Ready to Make Your Business <span className="gold-shimmer">Shine?</span>
            </h2>
            <p className="text-white/50 text-sm sm:text-base mb-10 max-w-2xl mx-auto">
              Get a free consultation and discover how our custom signage solutions can transform your business visibility.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking" className="bg-gold text-black font-bold px-8 sm:px-10 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2 text-sm sm:text-base">
                Book Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:+17783220801" className="bg-transparent border border-white/20 text-white px-8 sm:px-10 py-4 rounded-full hover:bg-white/5 hover:border-gold/50 transition-all flex items-center gap-2 text-sm sm:text-base">
                <Phone className="w-4 h-4" /> (778) 322-0801
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
